import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {Button, Text} from '@primer/react-brand/esm'
import {useEffect, useId, useMemo, useRef, useState, type ComponentProps, type Key, type ReactElement} from 'react'

import {announce} from '@github-ui/aria-live'

import {getAnalyticsEvent} from '../../lib/utils/analytics'
import {t} from '../../lib/utils/localization'

const COPIED_STATE_DURATION_MS = 2000

export type CopyToClipboard = (text: string) => Promise<void>
export type NativeShare = (data: ShareData) => Promise<void>

export type NativeShareCopy = {
  copiedMessage: string
  copiedTriggerLabel: string
  copyErrorMessage: string
  copyTriggerLabel: string
  copyUnsupportedMessage: string
  errorMessage: string
  triggerLabel: string
  unsupportedMessage: string
}

export type NativeShareActionOptions = {
  clipboardWrite?: CopyToClipboard | null
  copy?: NativeShareCopy
  share?: NativeShare | null
}

export type NativeShareActionController = {
  description?: string
  isCopied: boolean
  isCopyMode: boolean
  messageId: string
  onClick: () => Promise<void>
  triggerLabel: string
  visibleLabel: string
}

export type NativeShareActionAnalytics = {
  context?: string
  fallbackContext?: string
  location?: string
}

export type RenderNativeShareButtonOptions = {
  analytics?: NativeShareActionAnalytics
  block?: boolean
  className?: string
  controller: NativeShareActionController
  elementKey?: Key
  size?: ComponentProps<typeof Button>['size']
  variant?: ComponentProps<typeof Button>['variant']
}

export type NativeShareButtonElement = ReactElement<ComponentProps<typeof Button>, typeof Button>

type ShareMessage = {
  text: string
  type: 'error' | 'status'
}

export function getNativeShareCopy(triggerLabel?: string): NativeShareCopy {
  return {
    copiedMessage: t('Download link copied.'),
    copiedTriggerLabel: t('Copied'),
    copyErrorMessage: t("We couldn't copy the download link. Copy the current page URL from the address bar instead."),
    copyTriggerLabel: t('Copy download link'),
    copyUnsupportedMessage: t(
      "This browser doesn't support copying. Copy the current page URL from the address bar instead.",
    ),
    errorMessage: t("We couldn't share the download link. Copy it instead."),
    triggerLabel: triggerLabel?.trim() || t('Send me a download link'),
    unsupportedMessage: t("This browser doesn't support sharing. Copy the download link instead."),
  }
}

function isShareCancellation(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

export function useNativeShareAction({
  clipboardWrite,
  copy: configuredCopy,
  share: configuredShare,
}: NativeShareActionOptions = {}): NativeShareActionController {
  const copy = useMemo(() => configuredCopy ?? getNativeShareCopy(), [configuredCopy])
  const messageId = useId()
  const actionInProgressRef = useRef(false)
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [isCopied, setIsCopied] = useState(false)
  const [mode, setMode] = useState<'copy' | 'share'>('share')
  const [shareMessage, setShareMessage] = useState<ShareMessage | null>(null)

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current !== undefined) clearTimeout(copiedResetTimeoutRef.current)
    }
  }, [])

  function showShareMessage(message: ShareMessage) {
    setShareMessage(message)
    announce(message.text, {assertive: message.type === 'error'})
  }

  function showCopiedState() {
    setIsCopied(true)
    if (copiedResetTimeoutRef.current !== undefined) clearTimeout(copiedResetTimeoutRef.current)

    copiedResetTimeoutRef.current = setTimeout(() => {
      setIsCopied(false)
      copiedResetTimeoutRef.current = undefined
    }, COPIED_STATE_DURATION_MS)
  }

  async function shareCurrentPage() {
    if (actionInProgressRef.current) return

    const share =
      configuredShare === undefined
        ? typeof navigator === 'undefined' || typeof navigator.share !== 'function'
          ? null
          : (data: ShareData) => navigator.share(data)
        : configuredShare

    if (!share) {
      setMode('copy')
      showShareMessage({text: copy.unsupportedMessage, type: 'error'})
      return
    }

    actionInProgressRef.current = true
    setShareMessage(null)

    try {
      await share({url: window.location.href})
    } catch (error) {
      if (!isShareCancellation(error)) {
        setMode('copy')
        showShareMessage({text: copy.errorMessage, type: 'error'})
      }
    } finally {
      actionInProgressRef.current = false
    }
  }

  async function copyCurrentPage() {
    if (actionInProgressRef.current) return

    const copyToClipboard =
      clipboardWrite === undefined
        ? typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function'
          ? null
          : (text: string) => navigator.clipboard.writeText(text)
        : clipboardWrite

    if (!copyToClipboard) {
      showShareMessage({text: copy.copyUnsupportedMessage, type: 'error'})
      return
    }

    actionInProgressRef.current = true
    setShareMessage(null)

    try {
      await copyToClipboard(window.location.href)
      announce(copy.copiedMessage)
      showCopiedState()
    } catch {
      showShareMessage({text: copy.copyErrorMessage, type: 'error'})
    } finally {
      actionInProgressRef.current = false
    }
  }

  const isCopyMode = mode === 'copy'
  const triggerLabel = isCopyMode ? copy.copyTriggerLabel : copy.triggerLabel

  return {
    description: shareMessage?.text,
    isCopied,
    isCopyMode,
    messageId,
    onClick: isCopyMode ? copyCurrentPage : shareCurrentPage,
    triggerLabel,
    visibleLabel: isCopied ? copy.copiedTriggerLabel : triggerLabel,
  }
}

export function renderNativeShareButton({
  analytics,
  block,
  className,
  controller,
  elementKey,
  size,
  variant = 'primary',
}: RenderNativeShareButtonOptions): NativeShareButtonElement {
  return (
    <Button
      key={elementKey}
      aria-describedby={controller.description ? controller.messageId : undefined}
      block={block}
      className={className}
      leadingVisual={controller.isCopyMode ? controller.isCopied ? <CheckIcon /> : <CopyIcon /> : undefined}
      size={size}
      type="button"
      variant={variant}
      {...getAnalyticsEvent({
        action: controller.triggerLabel,
        tag: 'button',
        context: controller.isCopyMode ? (analytics?.fallbackContext ?? analytics?.context) : analytics?.context,
        location: analytics?.location,
      })}
      onClick={controller.onClick}
    >
      {controller.visibleLabel}
    </Button>
  )
}

export function renderNativeShareFeedback(controller: NativeShareActionController, className?: string) {
  if (!controller.description) return null

  return (
    <Text as="p" className={className} id={controller.messageId} size="100" variant="muted">
      {controller.description}
    </Text>
  )
}
